import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { COMPANY } from "@/lib/compliance";
import { CONTACT_TOPICS } from "@/lib/services";
import { submitInquiry, isValidEmail } from "./inquiry/submitInquiry";

const EMPTY = (defaultMessage) => ({
    full_name: "",
    email: "",
    phone: "",
    message: defaultMessage,
    topic: "",
    e_consent: false,
});

function validate(values) {
    const e = {};
    if (!values.full_name.trim()) e.full_name = "Please enter your name.";
    if (!values.email.trim()) e.email = "Email required.";
    else if (!isValidEmail(values.email)) e.email = "Enter a valid email.";
    if (!values.phone.trim() || values.phone.trim().length < 5)
        e.phone = "Phone required.";
    if (!values.message.trim()) e.message = "Tell us what you need.";
    if (!values.e_consent)
        e.e_consent = "Please agree to electronic communications.";
    return e;
}

/**
 * Compact in-page contact form. Triggered by any email-style link in
 * the UI. Submits through ``submitInquiry`` (same Proton SMTP +
 * EmailOctopus pipeline that the main inquiry form uses) so the lead
 * always reaches the agency mailbox even if the visitor has no email
 * client configured.
 */
export default function ContactDialog({ trigger, defaultMessage = "" }) {
    const [open, setOpen] = useState(false);
    const [values, setValues] = useState(() => EMPTY(defaultMessage));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const reset = () => {
        setValues(EMPTY(defaultMessage));
        setErrors({});
        setSubmitError("");
        setSent(false);
    };

    const handleOpenChange = (next) => {
        setOpen(next);
        if (!next) {
            // small delay so the closing animation doesn't show empty form flash
            setTimeout(reset, 250);
        }
    };

    const update = (k, v) => {
        setValues((s) => ({ ...s, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (submitting) return;
        setSubmitError("");
        const v = validate(values);
        if (Object.keys(v).length > 0) {
            setErrors(v);
            toast.error("Please fill in all required fields.");
            return;
        }
        setSubmitting(true);
        const subjectLine = values.topic
            ? `${values.topic} — ${values.message.trim().slice(0, 120)}`
            : values.message.trim();
        try {
            await submitInquiry({
                ...values,
                address: "(provided via Contact dialog)",
                help_with: subjectLine,
                source_url:
                    typeof window !== "undefined" ? window.location.href : "",
            });
            setSent(true);
        } catch {
            setSubmitError(
                "We couldn't send your message right now. Please call us at " +
                    COMPANY.phone +
                    " — and please do not email sensitive information."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent
                data-testid="contact-dialog"
                className="max-w-lg border border-[rgba(212,175,55,0.3)] bg-[#0b0e13] text-brand-ivory shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]"
            >
                {sent ? (
                    <DialogSuccess onClose={() => handleOpenChange(false)} />
                ) : (
                    <DialogFormBody
                        values={values}
                        errors={errors}
                        update={update}
                        submitting={submitting}
                        submitError={submitError}
                        onSubmit={onSubmit}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

function DialogSuccess({ onClose }) {
    return (
        <div data-testid="contact-dialog-success">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-brand-ivory">
                    <span className="inline-flex items-center gap-3">
                        <CheckCircle2 className="h-6 w-6 text-brand-gold2" />
                        Thank you.
                    </span>
                </DialogTitle>
                <DialogDescription className="mt-3 text-base text-brand-muted">
                    Your message was sent to {COMPANY.shortName}. A licensed
                    agent will review your request. Please do not send
                    sensitive information by unencrypted email or the general
                    contact form; call us if you need a secure channel.
                </DialogDescription>
            </DialogHeader>
            <div className="mt-6 flex items-center justify-between gap-3 text-sm text-brand-muted">
                <span>
                    Need us right away? Call{" "}
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        {COMPANY.phone}
                    </a>
                    .
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-full border border-brand-gold/60 px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Close
                </button>
            </div>
        </div>
    );
}

function DialogFormBody({
    values,
    errors,
    update,
    submitting,
    submitError,
    onSubmit,
}) {
    return (
        <form
            data-testid="contact-dialog-form"
            onSubmit={onSubmit}
            noValidate
        >
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-brand-ivory">
                    Email a licensed agent
                </DialogTitle>
                <DialogDescription className="text-sm text-brand-muted">
                    Sends your message directly to{" "}
                    <span className="text-brand-ivory">{COMPANY.email}</span>.
                    No mail app required.
                </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-3">
                <CompactField
                    id="cd_full_name"
                    label="Full Name"
                    error={errors.full_name}
                >
                    <input
                        id="cd_full_name"
                        data-testid="contact-dialog-name"
                        className="enc-input"
                        placeholder="Full Name"
                        value={values.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        autoComplete="name"
                        aria-required="true"
                        aria-invalid={!!errors.full_name}
                    />
                </CompactField>
                <CompactField
                    id="cd_email"
                    label="Your Email"
                    error={errors.email}
                >
                    <input
                        id="cd_email"
                        data-testid="contact-dialog-email"
                        type="email"
                        className="enc-input"
                        placeholder="you@example.com"
                        value={values.email}
                        onChange={(e) => update("email", e.target.value)}
                        autoComplete="email"
                        aria-required="true"
                        aria-invalid={!!errors.email}
                    />
                </CompactField>
                <CompactField
                    id="cd_phone"
                    label="Phone"
                    error={errors.phone}
                >
                    <input
                        id="cd_phone"
                        data-testid="contact-dialog-phone"
                        inputMode="tel"
                        className="enc-input"
                        placeholder="Phone Number"
                        value={values.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        autoComplete="tel"
                        aria-required="true"
                        aria-invalid={!!errors.phone}
                    />
                </CompactField>
                <CompactField
                    id="cd_message"
                    label="Message"
                    error={errors.message}
                >
                    <p
                        data-testid="contact-dialog-sensitive-warning"
                        className="mb-2 rounded-md border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.04)] px-3 py-2 text-[11px] leading-relaxed text-brand-ivory/85"
                    >
                        Please do not include Social Security numbers, payment
                        information, bank information, medical records, claim
                        documents, identity documents, passwords, or other
                        highly sensitive information.
                    </p>
                    <textarea
                        id="cd_message"
                        data-testid="contact-dialog-message"
                        className="enc-input min-h-[96px] resize-none"
                        placeholder="How can we help?"
                        value={values.message}
                        onChange={(e) => update("message", e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.message}
                    />
                </CompactField>

                <CompactField
                    id="cd_topic"
                    label="Topic (optional)"
                    error={null}
                >
                    <Select
                        value={values.topic}
                        onValueChange={(v) => update("topic", v)}
                    >
                        <SelectTrigger
                            id="cd_topic"
                            data-testid="contact-dialog-topic"
                            className="h-[44px] w-full border-[rgba(212,175,55,0.25)] bg-[#0b0e13] text-left text-brand-ivory hover:border-[rgba(212,175,55,0.45)] focus:border-brand-gold focus:ring-2 focus:ring-[rgba(212,175,55,0.15)]"
                        >
                            <SelectValue placeholder="Select a topic" />
                        </SelectTrigger>
                        <SelectContent className="border-[rgba(212,175,55,0.35)] bg-[#0b0e13] text-brand-ivory">
                            {CONTACT_TOPICS.map((opt) => (
                                <SelectItem
                                    key={opt}
                                    value={opt}
                                    data-testid={`contact-dialog-topic-option-${opt
                                        .toLowerCase()
                                        .replace(/[\s/]+/g, "-")}`}
                                    className="focus:bg-[rgba(212,175,55,0.12)] focus:text-brand-gold2"
                                >
                                    {opt}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CompactField>

                <ConsentCheckbox
                    checked={values.e_consent}
                    onChange={(v) => update("e_consent", v)}
                    error={errors.e_consent}
                />

                {submitError && (
                    <div
                        role="alert"
                        aria-live="assertive"
                        className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200"
                    >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" />
                        <p>{submitError}</p>
                    </div>
                )}
            </div>

            <button
                data-testid="contact-dialog-submit"
                type="submit"
                disabled={submitting}
                className="enc-submit mt-5 flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
            >
                {submitting ? "Sending…" : "Send Message"}
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </button>
            <p className="mt-3 text-center text-[11px] text-brand-muted">
                Or call{" "}
                <a
                    href={`tel:${COMPANY.phoneTel}`}
                    className="text-brand-gold2 underline-offset-4 hover:underline"
                >
                    {COMPANY.phone}
                </a>{" "}
                anytime.
            </p>
        </form>
    );
}

function CompactField({ id, label, error, children }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="mb-1.5 block text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft"
            >
                {label}
            </label>
            {children}
            {error && (
                <p role="alert" className="mt-1 text-xs text-red-400">
                    {error}
                </p>
            )}
        </div>
    );
}

function ConsentCheckbox({ checked, onChange, error }) {
    return (
        <>
            <label
                htmlFor="cd_econsent"
                className={`flex items-start gap-3 rounded-md border bg-[#0b0e13] p-3 text-xs leading-relaxed ${
                    error
                        ? "border-red-500/60"
                        : "border-[rgba(212,175,55,0.25)]"
                }`}
            >
                <input
                    id="cd_econsent"
                    data-testid="contact-dialog-econsent"
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#d4af37] focus-visible:ring-2 focus-visible:ring-brand-gold"
                />
                <span className="text-brand-muted">
                    <span className="text-brand-ivory">
                        Electronic communications consent.
                    </span>{" "}
                    I agree to be contacted by phone or email and to receive
                    electronic communications about my insurance inquiry. See
                    our{" "}
                    <Link
                        to="/privacy"
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        Privacy Notice
                    </Link>{" "}
                    and{" "}
                    <Link
                        to="/terms"
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        Terms
                    </Link>
                    .
                </span>
            </label>
            {error && (
                <p role="alert" className="text-xs text-red-400">
                    {error}
                </p>
            )}
        </>
    );
}
